import { Controller } from "stimulus";
import { storageManager } from "../utils/storage_manager";
import { deviceDetector } from "../utils/device_detector";
import { appStoreLinker } from "../utils/app_store_linker";
import { ScrollBehavior } from "../behaviors/scroll_behavior";

/**
 * DismissableBannerController - Unified controller for all dismissable banners
 *
 * Handles both app-banner and topic-bar with configuration via Stimulus values.
 * Supports two modes:
 * 1. App Banner: Mobile-only, device detection, app store linking
 * 2. Topic Bar: ID-based dismissal, scroll hide/show behavior
 *
 * Configuration via data attributes in HTML templates.
 */
export default class extends Controller {
  static targets = ["ctaLink"];

  static values = {
    type: String,              // 'app-banner' | 'topic-bar'
    storageKey: String,        // localStorage key
    visibilityClass: String,   // body class when visible
    hiddenClass: String,       // element class when hidden
    dismissalId: String,       // For ID-based dismissal (topic-bar)
    requiresMobile: Boolean,   // Only show on mobile (app-banner)
    scrollBehavior: String,    // 'track' | 'hide-show'
    scrollThreshold: Number,   // Scroll threshold in pixels
    scrollDelta: Number        // Minimum scroll delta for hide/show
  }

  /**
   * Initialize controller based on type
   */
  connect() {
    this.hidden = false;

    // Type-specific initialization
    if (this.isAppBanner()) {
      this.initializeAppBanner();
    } else if (this.isTopicBar()) {
      this.initializeTopicBar();
    }
  }

  /**
   * Initialize App Banner
   */
  initializeAppBanner() {
    if (!this.hasCtaLinkTarget) {
      console.warn('App banner: CTA link target not found');
      return;
    }

    if (!this.shouldShowAppBanner()) {
      this.hide();
      return;
    }

    this.setAppStoreUrl();
    document.body.classList.add(this.visibilityClassValue);
    this.initializeScrollTracking();
    this.handleScroll(); // Check initial state
  }

  /**
   * Initialize Topic Bar
   */
  initializeTopicBar() {
    if (this.isDismissed()) {
      this.hidden = true;
      this.element.style.display = 'none';
      return;
    }

    this.scrollBehavior = new ScrollBehavior({
      threshold: this.scrollThresholdValue || 50,
      delta: this.scrollDeltaValue || 5
    });

    this.hidden = false;
    document.body.classList.add(this.visibilityClassValue);
    this.initializeScrollTracking();
  }

  /**
   * Check if this is an app banner
   */
  isAppBanner() {
    return this.typeValue === 'app-banner';
  }

  /**
   * Check if this is a topic bar
   */
  isTopicBar() {
    return this.typeValue === 'topic-bar';
  }

  /**
   * Determine if app banner should be shown
   */
  shouldShowAppBanner() {
    // Guard against SSR
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }

    // Check dismissal
    if (this.isDismissed()) {
      return false;
    }

    // Mobile requirement
    if (this.requiresMobileValue) {
      if (deviceDetector.isStandalone()) return false;
      if (!deviceDetector.isMobile()) return false;
      if (deviceDetector.isIOSSafari()) return false;
    }

    return true;
  }

  /**
   * Set app store URL based on device
   */
  setAppStoreUrl() {
    if (!this.hasCtaLinkTarget) return;

    if (deviceDetector.isAndroid()) {
      const path = window.location.pathname;
      this.ctaLinkTarget.href = appStoreLinker.getAndroidIntentUrl(path);
    } else if (deviceDetector.isIOS()) {
      this.ctaLinkTarget.href = appStoreLinker.getIOSStoreUrl();
    } else {
      this.ctaLinkTarget.href = '#';
    }
  }

  /**
   * Initialize scroll event tracking
   */
  initializeScrollTracking() {
    this.lastScrollY = window.scrollY;
    this.scrollHandler = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  /**
   * Cleanup scroll tracking
   */
  cleanupScrollTracking() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  /**
   * Handle scroll events based on scroll behavior type
   */
  handleScroll() {
    if (this.scrollBehaviorValue === 'track') {
      this.handleTrackScroll();
    } else if (this.scrollBehaviorValue === 'hide-show') {
      this.handleHideShowScroll();
    }
  }

  /**
   * Track scroll position (app banner)
   * Adds class when scrolled past banner height
   */
  handleTrackScroll() {
    const bannerHeight = this.element.offsetHeight;
    const scrollY = window.scrollY;

    if (scrollY > bannerHeight) {
      document.body.classList.add('app-banner-scrolled');
    } else {
      document.body.classList.remove('app-banner-scrolled');
    }
  }

  /**
   * Hide/show based on scroll direction (topic bar)
   */
  handleHideShowScroll() {
    if (this.isDismissed()) {
      this.lastScrollY = window.scrollY;
      return;
    }

    const currentScrollY = window.scrollY;
    const action = this.scrollBehavior.getAction(
      currentScrollY,
      this.lastScrollY,
      this.scrollBehavior.threshold
    );

    if (action === 'hide' && !this.hidden) {
      this.hide();
    } else if (action === 'show' && this.hidden) {
      this.show();
    }

    this.lastScrollY = currentScrollY;
  }

  /**
   * Show the banner
   */
  show() {
    if (this.isDismissed()) return;

    this.hidden = false;
    this.element.classList.remove(this.hiddenClassValue);
    document.body.classList.add(this.visibilityClassValue);
  }

  /**
   * Hide the banner
   */
  hide() {
    this.hidden = true;
    this.element.classList.add(this.hiddenClassValue);
    document.body.classList.remove(this.visibilityClassValue);

    // App banner specific cleanup
    if (this.isAppBanner()) {
      document.body.classList.remove('app-banner-scrolled');
    }
  }

  /**
   * Close button handler (dismisses permanently)
   */
  close(event) {
    event.preventDefault();
    event.stopPropagation();
    this.hide();
    this.setDismissed();
  }

  /**
   * Handle CTA button click (app banner)
   */
  redirectToStore(event) {
    if (!this.hasCtaLinkTarget) return;

    event.preventDefault();
    const url = this.ctaLinkTarget.href;
    if (url && url !== '#') {
      window.location.href = url;
    }
  }

  /**
   * Check if banner was dismissed
   */
  isDismissed() {
    // ID-based dismissal (topic bar)
    if (this.hasDismissalIdValue) {
      const dismissedId = storageManager.getItem(this.storageKeyValue);
      return dismissedId === this.dismissalIdValue;
    }

    // Boolean dismissal (app banner)
    return storageManager.getBoolean(this.storageKeyValue, false);
  }

  /**
   * Mark banner as dismissed
   */
  setDismissed() {
    // ID-based dismissal (topic bar)
    if (this.hasDismissalIdValue) {
      storageManager.setItem(this.storageKeyValue, this.dismissalIdValue);
    } else {
      // Boolean dismissal (app banner)
      storageManager.setBoolean(this.storageKeyValue, true);
    }
  }

  /**
   * Cleanup on disconnect
   */
  disconnect() {
    this.cleanupScrollTracking();
    document.body.classList.remove(this.visibilityClassValue);

    if (this.isAppBanner()) {
      document.body.classList.remove('app-banner-scrolled');
    }
  }
}
